import {Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';

import {
    BehaviorSubject, combineLatest, Observable, of, Subscription,
    debounceTime, filter, map, startWith, switchMap, take, tap,
    Subject
} from 'rxjs';

import {Message, SelectItem} from 'primeng/api';
import {Dropdown} from 'primeng/dropdown';

import {DateTime} from 'luxon';

import {environment} from '@environment';

import {ActivityBlockingService} from '@shared/services/activity-blocking.service';
import {AutoSaveService} from '@shared/services/auto-save.service';
import {CarrierService} from '@shared/services/carrier.service';
import { MessageService } from '@shared/services/message-service';
import {CustomerService} from '@shared/services/customer.service';
import { WorkedShiftsService } from '@shared/services/worked-shifts.service';

import { parseDuration } from '@shared/helpers/parse-time';
import { fromFormControl } from '@shared/helpers/from-form-control';
import { timeFormat } from '@shared/helpers/timeFormat';
import { getSiteInfo } from '@shared/helpers/site-info';

import {Carrier} from '@shared/models/carrier';
import {CustomerAssignmentSummary} from '@shared/models/customer';
import {CustomerSite} from '@shared/models/customer-site';
import {Product} from '@shared/models/product';
import {SubinventoryType} from '@shared/models/subinventory';
import {ShiftStatus} from '@shared/models/worked-shift';
import { SiteConfig } from '@shared/models/site-config';

import {InboundLoadService} from '../../../services/inbound-load.service';
import {ProductService} from '../../../services/product.service';
import {ShiftContextService} from '../../../services/shift-context.service';
import { TrailerService } from '../../../services/trailer.service';
import {InboundLoad, InboundLoadLineItem} from '../../../models/inbound-load';
import {InboundLoadStatus} from '../../../models/inbound-load-status';
import {ShiftContext} from '../../../models/shift-context';
import { toTimestamp } from '../../../helpers/to-timestamp';

class FormLineItem {
    id?: number;
    productId?: number;
    quantity?: number;
    palletDescription?: string;
    isUnauthorized?: boolean;
}

@Component({
    selector: 'app-inbound-loads-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss']
})
export class InboundLoadsFormComponent implements OnInit, OnDestroy {
    InboundLoadStatus = InboundLoadStatus;

    readonly _inboundLoadLineItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Inbound Load Line Item',
        detail: 'Fields are required.'
    };

    readonly _inboundLoadLineItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Inbound Load Line Item Created',
        detail: ''
    };

    readonly _inboundLoadLineItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Inbound Load Line Item Deleted',
        detail: ''
    };

    pageName = 'InboundLoad';
    formDataName = 'InboundLoadForm';

    @ViewChild('poolerSelect', {static: false}) poolerSelect: Dropdown;
    @ViewChild('productSelect', {static: false}) productSelect: Dropdown;

    inboundLoadLineForm = new FormGroup({
        productId: new FormControl<number>(null, {validators: Validators.required, updateOn: 'submit'}),
        quantity: new FormControl<number>(0, Validators.required),
        palletDescription: new FormControl<string>('')
    });
    ilDefaultValuefields: { productId: null, quantity: 0, palletDescription: '' };

    inboundLoadForm = new FormGroup({
        inboundLoadStatus: new FormControl<InboundLoadStatus>(InboundLoadStatus.Arrived),
        siteSubinventoryId: new FormControl<number>(null, Validators.required),
        trailerId: new FormControl<number>(null, Validators.required),
        arrivedDate: new FormControl<Date>(null, Validators.required),
        arrivedTime: new FormControl<string>(null, Validators.required),
        carrierId: new FormControl<number>(null, Validators.required),
        carrierName: new FormControl<string>(''),
        customerId: new FormControl<number>(null, Validators.required),
        poolerId: new FormControl<number>(null, Validators.required),
        shipFromLocation: new FormControl<number>(null, Validators.required),
        vendorBolNumber: new FormControl<string>(''),
        propakPoNumber: new FormControl<string>(''),

        unloadedShiftId: new FormControl<number>(null),
        unloadedDate: new FormControl<Date>(null),
        unloadedTime: new FormControl<string>(null),
        processedShiftId: new FormControl<number>(null),
        stockTransfer: new FormControl<boolean>(false),
        isRejected: new FormControl<boolean>(false),
        rejectedBolNumber: new FormControl<string>(''),
        redTagFlag: new FormControl<boolean>(false),
        redTagReason: new FormControl<string>(''),
        comments: new FormControl<string>('')
    });

    products: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);

    documents: string[];

    showCarrierName: boolean;
    showPoNumber: boolean = false;
    isPoNumberRequired: boolean = false;
    isUnauthorized = false;
    displayAddShippedFromDialog: boolean;
    displayDialog: boolean;
    saving: boolean;
    autoSaveEnabled: boolean = false;
    entryFocus: boolean;

    existingLoadId: number | null = null;

    context = new BehaviorSubject<ShiftContext>(null);
    carriers = new BehaviorSubject<Carrier[]>([]);

    customerAssignments = new BehaviorSubject<CustomerAssignmentSummary>({customers: [], assignments: [], poolerRecentShipTos: {}});

    customers = combineLatest([
        this.customerAssignments,
        fromFormControl(this.inboundLoadForm.controls.siteSubinventoryId)
    ]).pipe(map(([customerAssignments, siteSubinventoryId]) => {
        const ids = customerAssignments.assignments
            .filter(a => a.siteSubinventoryId === siteSubinventoryId)
            .map(a => a.customerId);
        this.recentShipTos = customerAssignments.poolerRecentShipTos;
        return customerAssignments.customers.filter(c => c.isActive && c.isPrimary && ids.includes(c.id));
    }));

    poolers = combineLatest([
        this.customerAssignments,
        fromFormControl(this.inboundLoadForm.controls.siteSubinventoryId),
        fromFormControl(this.inboundLoadForm.controls.customerId)
    ]).pipe(map(([customerAssignments, siteSubinventoryId, customerId]) => {
        const ids = customerAssignments.assignments
            .filter(a => a.siteSubinventoryId === siteSubinventoryId && a.customerId === customerId)
            .map(a => a.poolerId);
        return customerAssignments.customers.filter(c => c.isActive && c.isPooler && ids.includes(c.id));
    }));

    siteSubinventories: SelectItem[] = [];
    carrierSelects = this.carriers.pipe(
        map(carriers => carriers.map(c => ({value: c.id, label: c.carrierCodeName}))));


    selectedPooler = combineLatest([this.poolers, this.inboundLoadForm.controls.poolerId.valueChanges])
        .pipe(map(([poolers, selectedId]) => {
            if (!!this.recentShipTos?.[selectedId])
                this.recentShipToIds = this.recentShipTos?.[selectedId];
            return poolers.find(p => p.id === selectedId);
        }));

    poolerName = this.selectedPooler.pipe(map(p => p?.accountName));

    poolerSites = this.selectedPooler.pipe(map(p => p?.customerSites || []));

    customersSelects = combineLatest([
        this.customers.pipe(startWith([])),
        fromFormControl(this.inboundLoadForm.controls.customerId)
    ]).pipe(map(([customers, customerId]) => {
        return [
            ...((!customerId || customers?.find(c => c.id === customerId)) ? [] : [{ value: customerId, label: '<Unknown Customer>' }]),
            ...customers.map(c => ({
                value: c.id,
                label: c.accountName
            }))
        ];
    }));

    poolersSelects = combineLatest([
        this.poolers.pipe(startWith([])),
        fromFormControl(this.inboundLoadForm.controls.poolerId)
    ]).pipe(map(([poolers, poolerId]) => {
        return [
            ...((!poolerId || poolers?.find(c => c.id === poolerId)) ? [] : [{ value: poolerId, label: '<Unknown Pooler>' }]),
            ...poolers.map(c => ({
                value: c.id,
                label: c.accountName
            }))
        ];
    }));

    poolerSitesSelects = combineLatest([
        this.poolerSites.pipe(startWith([])),
        this.customerAssignments,
        fromFormControl(this.inboundLoadForm.controls.customerId),
        fromFormControl(this.inboundLoadForm.controls.poolerId),
        fromFormControl(this.inboundLoadForm.controls.shipFromLocation)
    ]).pipe(map(([sites, assignments, customerId, poolerId, poolerSiteId]) => {
        const poolerAssignments = assignments.assignments.filter(a => a.customerId === customerId
            && a.poolerId === poolerId);

        return [
            { value: null, label: '-- New Site --', styleClass: 'add' },
            ...sites
                .filter(s => poolerAssignments.some(a => a.poolerSiteId === s.id || !a.poolerSiteId))
                .sort((a, b) => this.recentShipToIds.includes(a.id) ? -1 : 1)
                .map((s, index) => ({
                    value: s.id,
                    label: s.chepGlobalLocationId ? `${s.chepGlobalLocationId} - ${s.partySiteName}` : s.partySiteName,
                    styleClass: {
                        'recent': index === 0,
                        'all': index === this.recentShipToIds.length,
                        'text-strikethrough': s.activeFlag === false
                    }
                })),
            ...((!poolerSiteId || sites?.find(c => c.id === poolerSiteId)) ? [] : [{ value: poolerSiteId, label: '<Unknown Site>' }])
        ];
    }));

    sfGlidRequired: boolean;

    recentShipTos: { [key: string]: number[] };
    recentShipToIds: number[] = [];
    requestingBolNumber: boolean = false;

    selectedSubinventoryId = fromFormControl(this.inboundLoadForm.controls.siteSubinventoryId);

    selectedCarrier = combineLatest([
        fromFormControl(this.inboundLoadForm.controls.carrierId),
        this.carriers
    ]).pipe(
        map(([carrierId, carriers]) => carriers.find(c => c.id === carrierId)),
        filter(c => !!c));

    trailerEligibleFor: Observable<'arriving' | 'unloading'> = fromFormControl(this.inboundLoadForm.controls.inboundLoadStatus).pipe(
        map(status => {
            if (status === InboundLoadStatus.Processed)
                return 'unloading';
            else
                return 'arriving';
        }));

    productSelects = new BehaviorSubject<SelectItem<number | null>[]>([]);

    currentShipTo = combineLatest([
        this.customerAssignments,
        fromFormControl(this.inboundLoadForm.controls.poolerId),
        fromFormControl(this.inboundLoadForm.controls.shipFromLocation)
    ]).pipe(map(([customerAssignments, poolerId, poolerSiteId]) => customerAssignments.customers
        .find(c => c.id === poolerId)
        ?.customerSites
        ?.find(cs => cs.id === poolerSiteId)));

    showFullWorkflow = new BehaviorSubject<boolean>(false);

    loadTypeOptions = this.showFullWorkflow.pipe(map(showFullWorkflow => [
        {label: 'Planned', value: InboundLoadStatus.Planned},
        {label: 'Checked In', value: InboundLoadStatus.Arrived},
        ...(showFullWorkflow
            ? [
                {label: 'Unloaded', value: InboundLoadStatus.Unloaded},
                {label: 'Processed', value: InboundLoadStatus.Processed}
            ]
            : [
                {label: 'Unloaded', value: InboundLoadStatus.Processed}
            ]),
    ]));

    hasArrived = fromFormControl(this.inboundLoadForm.controls.inboundLoadStatus)
        .pipe(map(status => [InboundLoadStatus.Arrived, InboundLoadStatus.Unloaded, InboundLoadStatus.Processed].includes(status)));

    hasUnloaded = fromFormControl(this.inboundLoadForm.controls.inboundLoadStatus)
        .pipe(map(status => [InboundLoadStatus.Unloaded, InboundLoadStatus.Processed].includes(status)));

    isProcessed = fromFormControl(this.inboundLoadForm.controls.inboundLoadStatus)
        .pipe(map(status => status === InboundLoadStatus.Processed));

    supplierBOLRequired: boolean = false;
    siteTimezone: string = environment.fallbackTimeZone;

    // begin inbound load line item grid variables
    inboundLoadLineItems: FormLineItem[];

    subscriptions: Subscription[];
    shiftClosedSubscription: Subscription | null = null;


    constructor(private router: Router, private route: ActivatedRoute, private inboundLoadService: InboundLoadService,
                private carrierService: CarrierService, private productService: ProductService,
                private messageService: MessageService, private shiftContextService: ShiftContextService,
                private customerService: CustomerService, private trailerService: TrailerService,
                private autoSaveService: AutoSaveService, private activityService: ActivityBlockingService,
                private workedShiftService: WorkedShiftsService) {
    }

    updateFormState(loadStatus: InboundLoadStatus): void {
        this.inboundLoadForm.controls.arrivedDate.clearValidators();
        this.inboundLoadForm.controls.arrivedTime.clearValidators();
        this.inboundLoadForm.controls.unloadedShiftId.clearValidators();
        this.inboundLoadForm.controls.unloadedDate.clearValidators();
        this.inboundLoadForm.controls.unloadedTime.clearValidators();
        this.inboundLoadForm.controls.processedShiftId.clearValidators();
        this.inboundLoadForm.clearValidators();

        if (loadStatus !== InboundLoadStatus.Planned) {
            this.inboundLoadForm.controls.arrivedDate.setValidators(Validators.required);
            this.inboundLoadForm.controls.arrivedTime.setValidators(Validators.required);
        }

        if (loadStatus === InboundLoadStatus.Unloaded || loadStatus === InboundLoadStatus.Processed) {
            this.inboundLoadForm.controls.unloadedShiftId.setValidators(Validators.required);
            this.inboundLoadForm.controls.unloadedDate.setValidators(Validators.required);
            this.inboundLoadForm.controls.unloadedTime.setValidators(Validators.required);

            // unloaded timestamp after arrived
            this.inboundLoadForm.setAsyncValidators([
                async (form: typeof this.inboundLoadForm) => {
                    if (!form.controls.unloadedDate.value || !form.controls.arrivedDate.value)
                        return null;

                    var unloadDateTime = DateTime.fromJSDate(form.controls.unloadedDate.value)
                        .plus(parseDuration(form.controls.unloadedTime.value ?? '00:00'));

                    var arrivedDateTime = DateTime.fromJSDate(form.controls.arrivedDate.value)
                        .plus(parseDuration(form.controls.arrivedTime.value ?? '00:00'));

                    return arrivedDateTime > unloadDateTime
                        ? { timeSequence: { valid: false } }
                        : null;
                }
            ]);
        }

        if (loadStatus === InboundLoadStatus.Processed) {
            this.inboundLoadForm.controls.processedShiftId.setValidators(Validators.required);

            this.inboundLoadForm.setValidators([
                (_form: typeof this.inboundLoadForm) => {
                    return this.inboundLoadLineItems?.length > 0 ? null : { lineItemsExist: { valid: false } };
                }
            ]);
        }

        if (this.context.value) {
            switch (loadStatus) {
                case InboundLoadStatus.Planned:
                    this.inboundLoadForm.controls.arrivedDate.setValue(null);
                    this.inboundLoadForm.controls.arrivedTime.setValue(null);
                    this.inboundLoadForm.controls.unloadedShiftId.setValue(null);
                    this.inboundLoadForm.controls.unloadedDate.setValue(null);
                    this.inboundLoadForm.controls.unloadedTime.setValue(null);
                    this.inboundLoadForm.controls.processedShiftId.setValue(null);
                    break;
                case InboundLoadStatus.Arrived:
                    if (!this.inboundLoadForm.controls.arrivedDate.value)
                        this.inboundLoadForm.controls.arrivedDate.setValue(this.context.value.date.toJSDate());
                    if (!this.inboundLoadForm.controls.arrivedTime.value)
                        this.inboundLoadForm.controls.arrivedTime.setValue(DateTime.local().toFormat('HH:mm'));

                    this.inboundLoadForm.controls.unloadedShiftId.setValue(null);
                    this.inboundLoadForm.controls.unloadedDate.setValue(null);
                    this.inboundLoadForm.controls.unloadedTime.setValue(null);
                    this.inboundLoadForm.controls.processedShiftId.setValue(null);
                    break;
                case InboundLoadStatus.Unloaded:
                    if (!this.inboundLoadForm.controls.arrivedDate.value)
                        this.inboundLoadForm.controls.arrivedDate.setValue(this.context.value.date.toJSDate());
                    if (!this.inboundLoadForm.controls.arrivedTime.value)
                        this.inboundLoadForm.controls.arrivedTime.setValue(DateTime.local().toFormat('HH:mm'));

                    if (!this.inboundLoadForm.controls.unloadedShiftId.value)
                        this.inboundLoadForm.controls.unloadedShiftId.setValue(this.context.value.id);
                    if (!this.inboundLoadForm.controls.unloadedDate.value)
                        this.inboundLoadForm.controls.unloadedDate.setValue(this.context.value.date.toJSDate());
                    if (!this.inboundLoadForm.controls.unloadedTime.value)
                        this.inboundLoadForm.controls.unloadedTime.setValue(DateTime.local().toFormat('HH:mm'));

                    this.inboundLoadForm.controls.processedShiftId.setValue(null);
                    break;
                case InboundLoadStatus.Processed:
                    if (!this.inboundLoadForm.controls.arrivedDate.value)
                        this.inboundLoadForm.controls.arrivedDate.setValue(this.context.value.date.toJSDate());
                    if (!this.inboundLoadForm.controls.arrivedTime.value)
                        this.inboundLoadForm.controls.arrivedTime.setValue(DateTime.local().toFormat('HH:mm'));
                    if (!this.inboundLoadForm.controls.unloadedShiftId.value)
                        this.inboundLoadForm.controls.unloadedShiftId.setValue(this.context.value.id);
                    if (!this.inboundLoadForm.controls.unloadedDate.value)
                        this.inboundLoadForm.controls.unloadedDate.setValue(this.context.value.date.toJSDate());
                    if (!this.inboundLoadForm.controls.unloadedTime.value)
                        this.inboundLoadForm.controls.unloadedTime.setValue(DateTime.local().toFormat('HH:mm'));

                    if (!this.inboundLoadForm.controls.processedShiftId.value)
                        this.inboundLoadForm.controls.processedShiftId.setValue(this.context.value.id);
                    break;
            }
        }

        this.inboundLoadForm.controls.arrivedDate.updateValueAndValidity();
        this.inboundLoadForm.controls.arrivedTime.updateValueAndValidity();
        this.inboundLoadForm.controls.unloadedShiftId.updateValueAndValidity();
        this.inboundLoadForm.controls.unloadedDate.updateValueAndValidity();
        this.inboundLoadForm.controls.unloadedTime.updateValueAndValidity();
        this.inboundLoadForm.controls.processedShiftId.updateValueAndValidity();
        this.inboundLoadForm.updateValueAndValidity();
    }

    ngOnInit(): void {
        this.inboundLoadForm.controls.vendorBolNumber.setAsyncValidators(control => {
            if (!this.inboundLoadForm.controls.customerId.value || !control.value)
                return Promise.resolve(null);

            return this.inboundLoadService.refNumberInUse(this.inboundLoadForm.controls.customerId.value, control.value)
                .pipe(map(inUse => {
                    if (!!inUse && (!this.existingLoadId || this.existingLoadId !== inUse))
                        return {numberInUse: {valid: false}};
                    else
                        return null;
                }));
        });

        this.inboundLoadForm.controls.carrierName.setValidators(control => {
            const currentCarrier = this.carriers.value.find(c => c.id === this.inboundLoadForm.controls.carrierId.value);
            return (currentCarrier?.carrierCode !== 'NOSC' && currentCarrier?.carrierCode !== 'CUPU') || control.value
                ? null
                : {requiredIfNosc: true};
        });

        this.updateFormState(InboundLoadStatus.Arrived);

        const carriersCompleteSubject = new Subject<void>();
        const contextCompleteSubject = new Subject<void>();

        this.subscriptions = [
            // keep this observable first so taht the subjects fire correctly
            combineLatest([ carriersCompleteSubject, contextCompleteSubject ])
                .pipe(
                    take(1),
                    switchMap(_ => {
                        return this.initializeForm();
                    }),
                    switchMap(_ => {
                        if (this.route.snapshot.queryParamMap.has('trailerId')) {
                            const trailerId = +this.route.snapshot.queryParamMap.get('trailerId');

                            return this.trailerService.getTrailer(trailerId).pipe(tap(trailer => {
                                if (trailer) {
                                    this.inboundLoadForm.patchValue({
                                        siteSubinventoryId: trailer.siteSubinventoryId,
                                        carrierId: trailer.carrierId,
                                        trailerId: trailer.id
                                    })
                                }
                            }));
                        }
                        else {
                            return of(null);
                        }
                    }),
                    tap(_ => {
                        this.autoSaveEnabled = true;
                    }))
                .subscribe(),

            this.shiftContextService.SiteConfig.subscribe(configs => {
                this.siteTimezone = configs
                    .find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)
                    ?.GetValue<string>()
                    ?? environment.fallbackTimeZone;
            }),

            this.productService.CurrentProducts.subscribe(products => {
                this.products.next(products);
            }),

            this.carrierService.getCarriers()
                .pipe(this.activityService.Wait('Inbound Load Form - Carriers'))
                .subscribe(carriers => {
                    this.setCarriers(carriers);
                    carriersCompleteSubject.next();
                }),

            this.inboundLoadForm.valueChanges.subscribe(_value => {
                this.autoSaveServiceSetItem();
            }),

            fromFormControl(this.inboundLoadForm.controls.inboundLoadStatus).subscribe(loadType => {
                this.updateFormState(loadType);
            }),

            combineLatest([
                fromFormControl(this.inboundLoadForm.controls.customerId),
                fromFormControl(this.inboundLoadForm.controls.siteSubinventoryId),
                this.customerAssignments
            ]).subscribe(([customerId, subinvId, assignments]) => {
                const isOwnPooler = assignments.assignments
                    .some(a => a.siteSubinventoryId === subinvId
                        && a.customerId === customerId
                        && a.poolerId === customerId);

                if (isOwnPooler) {
                    this.inboundLoadForm.controls.poolerId.setValue(customerId);
                }
            }),

            combineLatest([
                fromFormControl(this.inboundLoadForm.controls.siteSubinventoryId),
                this.shiftContextService.CurrentContext
            ]).subscribe(([subinvId, context]) => {
                const subinv = context.site.subinventories.find(s => s.id === subinvId);
                this.showFullWorkflow.next([
                    SubinventoryType.PecoPallets,
                    SubinventoryType.PecoComponents,
                    SubinventoryType.IgpsPallets
                ].includes(subinv?.subinventoryType));
            }),

            combineLatest([this.inboundLoadForm.controls.customerId.valueChanges, this.customers]).subscribe(([value, customers]) => {
                const customer = customers.find(s => s.id === value);

                if (customer) {
                    this.inboundLoadForm.controls.vendorBolNumber.clearValidators();

                    this.supplierBOLRequired = customer.isCustomerRefRequired;

                    if (this.supplierBOLRequired)
                        this.inboundLoadForm.controls.vendorBolNumber.setValidators(Validators.required);

                    this.inboundLoadForm.controls.vendorBolNumber.updateValueAndValidity();
                }
            }),

            this.shiftContextService.CurrentContext
                .pipe(this.activityService.Wait('Inbound Load Form - Site Context'))
                .subscribe(context => {
                    this.context.next(context);

                    this.siteSubinventories = context.site.subinventories
                        .filter(s => s.isActive)
                        .map(si => ({value: si.id, label: si.name}));

                    if (this.siteSubinventories.some(s => s.value !== this.inboundLoadForm.controls.siteSubinventoryId.value))
                        this.inboundLoadForm.controls.siteSubinventoryId.setValue(null);

                    contextCompleteSubject.next();
                }),

            combineLatest([
                this.shiftContextService.CurrentContext,
                fromFormControl(this.inboundLoadForm.controls.processedShiftId).pipe(
                    switchMap(shiftId => shiftId ? this.workedShiftService.FollowById(shiftId) : of(null))),
            ])
                .pipe(switchMap(([context, processedShift]) => this.customerService.getCustomers(context.site.id, processedShift?.date, 'inbound')))
                .subscribe(c => this.customerAssignments.next(c)),

            this.inboundLoadForm.controls.carrierId.valueChanges.subscribe(carrierId => {
                const currentCarrier = this.carriers.value.find(c => c.id === carrierId);
                this.showCarrierName = currentCarrier?.carrierCode === 'NOSC' || currentCarrier?.carrierCode === 'CUPU';
                this.inboundLoadForm.controls.carrierName.updateValueAndValidity();
            }),

            combineLatest([
                fromFormControl(this.inboundLoadForm.controls.unloadedShiftId),
                this.showFullWorkflow
            ]).subscribe(([shiftId, showFullWorkflow]) => {
                if (!showFullWorkflow && this.inboundLoadForm.controls.inboundLoadStatus.value === InboundLoadStatus.Processed)
                    this.inboundLoadForm.controls.processedShiftId.setValue(shiftId);
            }),

            combineLatest([
                fromFormControl(this.inboundLoadForm.controls.siteSubinventoryId),
                fromFormControl(this.inboundLoadForm.controls.customerId),
                fromFormControl(this.inboundLoadForm.controls.poolerId),
                fromFormControl(this.inboundLoadForm.controls.shipFromLocation),
                this.customerAssignments,
                this.products
            ]).pipe(
                debounceTime(100),
                map(([ subinvId, customerId, poolerId, poolerSiteId, assignments, products ]) => {
                    if (subinvId && customerId && poolerId && poolerSiteId) {
                        const productIds = assignments?.assignments
                            .filter(a => a.customerId == customerId
                                && a.siteSubinventoryId == subinvId
                                && a.poolerId == poolerId
                                && (a.poolerSiteId == poolerSiteId || a.poolerSiteId == null))
                            .flatMap(a => a.productIds)
                            ?? [];

                        return [
                            { value: null, label: '-- Select Product --'},
                            ...products.filter(p => productIds.includes(p.id)).map(p => ({value: p.id, label: p.label}))
                        ];
                    }
                    else {
                        return [];
                    }
                }))
                .subscribe(productSelects => {
                    this.productSelects.next(productSelects);
                }),

            combineLatest([
                fromFormControl(this.inboundLoadForm.controls.siteSubinventoryId),
                fromFormControl(this.inboundLoadForm.controls.inboundLoadStatus),
                this.context
            ]).subscribe(([subinvId, status, context]) => {
                this.isPoNumberRequired = false;
                this.inboundLoadForm.controls.propakPoNumber.clearValidators();

                const subinv = context?.site.subinventories.find(ss => ss.id === subinvId);

                if (subinv?.subinventoryType === SubinventoryType.ChepComponents
                    || subinv?.subinventoryType === SubinventoryType.WhiteWoodComponents) {
                    this.showPoNumber = true;

                    if (status === InboundLoadStatus.Processed) {
                        this.inboundLoadForm.controls.propakPoNumber.setValidators(Validators.required);
                        this.isPoNumberRequired = true;
                    }
                }
                else {
                    this.showPoNumber = false;
                }

                this.inboundLoadForm.controls.propakPoNumber.updateValueAndValidity();
            })
        ];
    }

    setInboundLoadFormValues(load: InboundLoad): void {
        this.existingLoadId = load.id ?? null;

        this.updateFormState(load.inboundLoadStatus);

        const values = {
            inboundLoadStatus: load.inboundLoadStatus,
            siteSubinventoryId: load.siteSubinventoryId,
            trailerId: load.trailerId,

            arrivedDate: load.arrivedTimestamp?.startOf('day').toJSDate() ?? null,
            arrivedTime: load.arrivedTimestamp ? timeFormat(load.arrivedTimestamp?.setZone(this.siteTimezone)) : null,

            unloadedShiftId: [InboundLoadStatus.Unloaded, InboundLoadStatus.Processed].includes(load.inboundLoadStatus)
                ? load.unloadedWorkedShiftId
                : null,
            unloadedDate: load.unloadedTimestamp?.startOf('day').toJSDate() ?? null,
            unloadedTime: load.unloadedTimestamp ? timeFormat(load.unloadedTimestamp?.setZone(this.siteTimezone)) : null,

            processedShiftId: load.inboundLoadStatus === InboundLoadStatus.Processed ? load.processedWorkedShiftId : null,

            carrierId: load.carrierId,
            carrierName: load.carrierName || '',
            customerId: load.customerId || null,
            poolerId: load.poolerId || null,
            shipFromLocation: load.customerSiteId || null,
            vendorBolNumber: load.vendorBolNumber,
            propakPoNumber: load.propakPoNumber || '',
            comments: load.comments || '',
            stockTransfer: !!load.stockTransfer,
            isRejected: load.isRejected || false,
            rejectedBolNumber: load.rejectedBolNumber || '',
            redTagFlag: load.redTagFlag || false,
            redTagReason: load.redTagReason || ''
        };

        this.inboundLoadLineItems = (load.inboundLoadLineItems || []).map(li => ({
            id: li.id,
            productId: li.productId,
            quantity: li.quantity,
            palletDescription: li.palletDescription,
            isUnauthorized: li.isUnauthorized
        }));

        this.inboundLoadForm.setValue(values, { emitEvent: true });

        this.inboundLoadForm.updateValueAndValidity({ emitEvent: true });

        this.documents = load.documentIds || [];

        this.shiftClosedSubscription?.unsubscribe();
        this.shiftClosedSubscription = null;

        if (load.inboundLoadStatus === InboundLoadStatus.Processed) {
            this.shiftClosedSubscription = this.workedShiftService.FollowById(load.processedWorkedShiftId).subscribe(shift => {
                if (shift.shiftStatus === ShiftStatus.Closed || shift.shiftStatus === ShiftStatus.Locked) {
                    this.inboundLoadForm.disable();
                    this.inboundLoadLineForm.disable();
                }
                else {
                    this.inboundLoadForm.enable();
                    this.inboundLoadLineForm.enable();
                }
            });
        }
        else {
            this.inboundLoadForm.enable();
            this.inboundLoadLineForm.enable();
        }
    }

    initializeForm(): Observable<any> {
        const autoSave = this.autoSaveService.GetItem<InboundLoad>(this.pageName, this.formDataName);

        if (this.route.snapshot.paramMap.has('id')) {
            const id = this.route.snapshot.paramMap.get('id');
            return this.inboundLoadService.getLoadDetail(id).pipe(tap(load => {
                this.setInboundLoadFormValues(load);
            }));
        } else if (autoSave) {
            try {
                this.setInboundLoadFormValues(autoSave);
                this.inboundLoadForm.markAllAsTouched();
                this.messageService.add(this.autoSaveService.DataRestoredMsg);
                return of(true);
            }
            catch {
                this.autoSaveService.RemoveItem(this.pageName, this.formDataName);
            }
        }

        this.setInboundLoadFormValues({
            id: 0,
            inboundLoadStatus: InboundLoadStatus.Arrived,
            siteId: this.context.value?.site.id,
            siteSubinventoryId: null,
            trailerId: null,
            arrivedTimestamp: null,
            unloadedWorkedShiftId: null,
            processedWorkedShiftId: null,
            unloadedTimestamp: null,
            customerId: null,
            poolerId: null,
            customerSiteId: null,
            carrierId: null,
            carrierName: null,
            vendorBolNumber: '',
            propakPoNumber: '',
            stockTransfer: false,
            isRejected: false,
            rejectedBolNumber: '',
            redTagFlag: false,
            redTagReason: '',
            inboundLoadLineItems: [],
            comments: null
        })

        // initialize to current date/shift
        return this.shiftContextService.CurrentContext.pipe(take(1), tap(context => {
            this.inboundLoadForm.controls.arrivedDate.setValue(context.date.toJSDate());
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
        this.shiftClosedSubscription?.unsubscribe();
        this.shiftClosedSubscription = null;

        const inboundLoadForm = this.autoSaveService.GetItem<InboundLoad>(this.pageName, this.formDataName);

        if (!this.route.snapshot.paramMap.has('id') && inboundLoadForm) {
            this.messageService.add(this.autoSaveService.DataSavedMsg);
        }

        this.carriers.next([]);
        this.customerAssignments.next({customers: [], assignments: [], poolerRecentShipTos: {}});
    }

    onUnloadedShiftDateChanged(date: DateTime): void {
        if (date)
            this.inboundLoadForm.controls.unloadedDate.setValue(date.toJSDate());
    }

    createLineItemProductSelects(row: FormLineItem): Observable<SelectItem<number | null>[]> {
        return combineLatest([
            this.productSelects.pipe(startWith([])),
            this.products.pipe(filter(p => !!p), startWith([]))
        ]).pipe(map(([selects, products]) => {
            return selects.some(p => p.value === row.productId)
                ? selects
                : [
                    ...selects,
                    {
                        value: row.productId,
                        label: products.find(product => product.id === row.productId)?.label ?? '-- Unknown Product --'
                    }
                ];
        }));
    }

    setCarriers(carriers: Carrier[]): void {
        this.carriers.next(carriers.slice().sort((l, r) => {
            const left = l.carrierCode === 'NOSC' || l.carrierCode === 'CUPU' ? '' : l.carrierCodeName;
            const right = r.carrierCode === 'NOSC' || r.carrierCode === 'CUPU' ? '' : r.carrierCodeName;
            return left.localeCompare(right);
        }));

        if (this.carriers.value.length === 1)
            this.inboundLoadForm.controls.carrierId.setValue(this.carriers.value[0].id);
        else if (!this.carriers.value.find(c => c.id === this.inboundLoadForm.controls.carrierId.value))
            this.inboundLoadForm.controls.carrierId.setValue(null);
    }

    showDropdown(_e: any): void {
        if (!this.entryFocus) {
            this.poolerSelect.show();
            this.entryFocus = true;
        } else {
            // set to false due to dropdown focus interaction activating dropdown loop.
            this.entryFocus = false;
        }
    }

    hideDropdown(_e: any): void {
        if (this.entryFocus) {
            this.poolerSelect.hide();
        }
    }

    showAddCarrierDialog(): void {
        this.displayDialog = true;
    }

    closeAddCarrierDialog(dialogClosed: boolean): void {
        this.displayDialog = dialogClosed;
    }

    onCarrierCreated(newCarrier: Carrier): void {
        this.setCarriers([...this.carriers.value, newCarrier]);

        this.inboundLoadForm.controls.carrierId.setValue(newCarrier.id);
    }

    showAddPoolerSiteDialog(): void {
        this.displayAddShippedFromDialog = true;
    }

    onPoolerSiteChanged(site: CustomerSite): void {
        this.inboundLoadForm.controls.poolerId.setValue(site.customerId);
        const pooler = this.customerAssignments.value.customers.find(p => p.id === site.customerId);

        // NOTE(nicholas-ramsey): The `site` may be a pre-existing site selected from the duplicate list
        pooler.customerSites = [...pooler.customerSites.filter(s => s.id !== site.id), site];

        this.customerAssignments.next(this.customerAssignments.value);

        this.inboundLoadForm.controls.shipFromLocation.setValue(site.id);
    }

    shipToLocationSelected(): void {
        const shipTo = this.customerAssignments.value.customers
            .find(c => c.id === this.inboundLoadForm.controls.poolerId.value)
            ?.customerSites
            ?.find(cs => cs.id === this.inboundLoadForm.controls.shipFromLocation.value);

        this.inboundLoadForm.controls.stockTransfer.setValue(shipTo?.isStockTransferSite ?? false);
    }

    getBolNumber(): void {
        this.requestingBolNumber = true;

        if (this.context.value) {
            this.inboundLoadService.getNextBolNumber(this.context.value.site.id).subscribe({
                next: bolNum => {
                    this.inboundLoadForm.controls.vendorBolNumber.setValue(bolNum);
                    this.requestingBolNumber = false;
                },
                error: _err => {

                    this.requestingBolNumber = false;
                }
            });
        }
    }

    onNewLineProductChange(event: { value: number }): void {
        this.inboundLoadLineForm.get('productId').setValue(event.value);
        const prod = this.products.value.find(p => p.id === event.value);
        if (prod.productType === 'Unauthorized') {
            this.isUnauthorized = true;
            this.inboundLoadLineForm.get('palletDescription').enable();
            this.inboundLoadLineForm.controls.palletDescription.setValue('');
        } else {
            this.inboundLoadLineForm.controls.quantity.setValidators([
                Validators.required,
                Validators.min(1),
                Validators.max(prod.maxLoadQuantity)
            ]);

            this.inboundLoadLineForm.controls.quantity.updateValueAndValidity();

            this.isUnauthorized = false;
            this.inboundLoadLineForm.get('palletDescription').disable();
            this.inboundLoadLineForm.controls.palletDescription.setValue('');
        }
    }

    // begin inbound load line item grid methods
    onChangeInboundLoadField(inboundLoadLineItem: InboundLoadLineItem): void {
        const product = this.products.value.find(p => p.id === inboundLoadLineItem.productId);
        if (product.productType === 'Unauthorized') {
            inboundLoadLineItem.isUnauthorized = true;
        } else {
            inboundLoadLineItem.isUnauthorized = false;
            inboundLoadLineItem.palletDescription = '';
        }
        if (String(inboundLoadLineItem.quantity) === '') {
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Update Line Item',
                detail: 'Quantity is required.'
            });
        } else {
            if (inboundLoadLineItem.quantity >= 1 &&
                inboundLoadLineItem.quantity <= product.maxLoadQuantity) {
                if (product.productType === 'Unauthorized'
                    && inboundLoadLineItem.palletDescription === '') {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Could not Update Line Item',
                        detail: 'Description is required.'
                    });
                } else {
                    this.messageService.add({
                        severity: 'success', summary: 'Line Item Updated',
                        detail: 'Save the load to complete changes.'
                    });
                    this.autoSaveServiceSetItem();
                }
            } else {
                if (+inboundLoadLineItem.quantity === 0) {
                    this.messageService.add({
                        severity: 'warn', summary: 'Could not Create Line Item',
                        detail: 'Quantity must not be zero.'
                    });
                } else {
                    this.messageService.add({
                        severity: 'warn', summary: 'Could not Create Line Item',
                        detail: `Max quantity for item exceeded.\n Quantity should be less than ${product.maxLoadQuantity}.`
                    });
                }
            }
        }
    }

    saveILLI(): void {
        if (!this.inboundLoadLineForm.valid) {
            this.inboundLoadLineForm.markAllAsTouched();
            this.messageService.add(this._inboundLoadLineItemNotCreatedMsg);
        } else {
            const product = this.products.value.find(p => p.id === this.inboundLoadLineForm.get('productId').value);
            if (this.inboundLoadLineForm.controls.quantity.value < 1)
                this.messageService.add({
                    severity: 'warn', summary: 'Could not Create Line Item',
                    detail: 'Quantity must not be zero.'
                });
            else if (product.maxLoadQuantity
                && this.inboundLoadLineForm.controls.quantity.value >
                product.maxLoadQuantity)
                this.messageService.add({
                    severity: 'warn', summary: 'Could not Create Line Item',
                    detail: `Max quantity for item exceeded.\n Quantity should be less than ${product.maxLoadQuantity}.`
                });
            else {
                if (product.productType === 'Unauthorized' &&
                    this.inboundLoadLineForm.get('palletDescription').value === '') {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Could not Update Line Item',
                        detail: 'Description is required.'
                    });
                } else {
                    const entry: FormLineItem = {...this.inboundLoadLineForm.value};
                    if (product.productType === 'Unauthorized') {
                        entry.isUnauthorized = true;
                    } else {
                        entry.isUnauthorized = false;
                    }
                    this.inboundLoadLineItems = [...(this.inboundLoadLineItems || []), entry];
                    this.isUnauthorized = false;
                    this.inboundLoadLineForm.reset(this.ilDefaultValuefields);
                    this.messageService.add(this._inboundLoadLineItemCreatedMsg);
                    this.autoSaveServiceSetItem();
                }
            }
            this.inboundLoadForm.updateValueAndValidity();
            this.productSelect.focus();
        }
    }

    onInboundLoadRowCancel(inboundLoadLineItem: FormLineItem): void {
        const index = this.inboundLoadLineItems.indexOf(inboundLoadLineItem);
        this.inboundLoadLineItems = this.inboundLoadLineItems.filter((_val, i) => i !== index);
        this.messageService.add(this._inboundLoadLineItemDeletedMsg);
        this.autoSaveServiceSetItem();

        this.inboundLoadForm.updateValueAndValidity();
    }

    // end inbound load line item grid methods

    createLoadObject(): InboundLoad {
        const currentCarrier = this.carriers.value.find(c => c.id === this.inboundLoadForm.controls.carrierId.value);

        const formData: InboundLoad = {
            id: this.existingLoadId ?? 0,
            siteId: this.context.value?.site.id,
            siteSubinventoryId: this.inboundLoadForm.controls.siteSubinventoryId.value,
            inboundLoadStatus: this.inboundLoadForm.controls.inboundLoadStatus.value,
            vendorBolNumber: this.inboundLoadForm.controls.vendorBolNumber.value ?? '',
            propakPoNumber: this.inboundLoadForm.controls.propakPoNumber.value,
            trailerId: this.inboundLoadForm.controls.trailerId.value,

            arrivedTimestamp: null,
            unloadedWorkedShiftId: null,
            unloadedTimestamp: null,
            processedWorkedShiftId: null,

            carrierId: this.inboundLoadForm.controls.carrierId.value,
            carrierName: ((currentCarrier?.carrierCode === 'NOSC' || currentCarrier?.carrierCode === 'CUPU')
                ? this.inboundLoadForm.controls.carrierName.value : null) || '',
            customerId: this.inboundLoadForm.controls.customerId.value,
            poolerId: this.inboundLoadForm.controls.poolerId.value,
            customerSiteId: this.inboundLoadForm.controls.shipFromLocation.value,
            stockTransfer: !!this.inboundLoadForm.controls.stockTransfer.value,
            comments: this.inboundLoadForm.controls.comments.value,

            isRejected: this.inboundLoadForm.controls.isRejected.value,
            rejectedBolNumber: this.inboundLoadForm.controls.rejectedBolNumber.value,
            redTagFlag: this.inboundLoadForm.controls.redTagFlag.value,
            redTagReason: this.inboundLoadForm.controls.redTagReason.value,

            inboundLoadLineItems: []
        };

        if ([InboundLoadStatus.Arrived, InboundLoadStatus.Unloaded, InboundLoadStatus.Processed].includes(formData.inboundLoadStatus)) {
            formData.arrivedTimestamp = toTimestamp(this.inboundLoadForm.controls.arrivedDate.value, this.inboundLoadForm.controls.arrivedTime.value, this.siteTimezone);
        }

        if ([InboundLoadStatus.Unloaded, InboundLoadStatus.Processed].includes(formData.inboundLoadStatus)) {
            formData.unloadedWorkedShiftId = this.inboundLoadForm.controls.unloadedShiftId.value ?? null;
            formData.unloadedTimestamp = toTimestamp(this.inboundLoadForm.controls.unloadedDate.value, this.inboundLoadForm.controls.unloadedTime.value, this.siteTimezone);
        }

        if (formData.inboundLoadStatus === InboundLoadStatus.Processed) {
            formData.processedWorkedShiftId = this.inboundLoadForm.controls.processedShiftId.value ?? null;

            formData.inboundLoadLineItems = (this.inboundLoadLineItems || []).map(li => ({
                id: li.id,
                productId: li.productId,
                quantity: +li.quantity,
                palletDescription: li.palletDescription,
                isUnauthorized: li.isUnauthorized
            }));
        }

        return formData;
    }

    saveLoad(): void {
        if (!this.inboundLoadForm.valid) {
            this.inboundLoadForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Could not Create Load',
                detail: 'Please correct the listed errors.'
            });

            return;
        }

        this.saving = true;

        const formData = this.createLoadObject();

        let observable: Observable<Message>;

        if (this.existingLoadId) {
            observable = this.updateInboundLoad(formData);
        } else {
            observable = this.createInboundLoad(formData);
        }

        observable.subscribe(message => {
            this.messageService.add(message);

            this.saving = false;
            this.autoSaveService.RemoveItem(this.pageName, this.formDataName);

            if (this.route.snapshot.queryParamMap.has('fromTrailerForm'))
                this.router.navigateByUrl('/trailers');
            else
                this.router.navigateByUrl('/loads/inbound');

            }, (_error: HttpErrorResponse) => {
            this.saving = false;
        });
    }

    createInboundLoad(formData: InboundLoad): Observable<Message> {
        return this.inboundLoadService.createInboundLoad(formData).pipe(map(_result => ({
            severity: 'success',
            summary: 'Load Created',
            detail: `Load ${this.inboundLoadForm.controls.vendorBolNumber.value} has been saved.`
        })));
    }

    updateInboundLoad(formData: InboundLoad): Observable<Message> {
        formData.id = this.existingLoadId;

        return this.inboundLoadService.updateInboundLoad(formData).pipe(map(_result => ({
            severity: 'success',
            summary: 'Load Updated',
            detail: `Load ${this.inboundLoadForm.controls.vendorBolNumber.value} has been saved.`
        })));
    }

    autoSaveServiceSetItem(): void {
        const data = this.createLoadObject();
        if (!this.route.snapshot.paramMap.has('id') && this.autoSaveEnabled) {
            this.autoSaveService.SetItem(this.pageName, this.formDataName, data);
        }
    }

    parseDateTime(date: Date): DateTime {
        const dt = DateTime.fromJSDate(date);

        return dt.isValid ? dt : null;
    }

    resetInboundLoadAutoSave(): void {
        this.autoSaveService.RemoveItem(this.pageName, this.formDataName);
        this.initializeForm().subscribe(() => {
            this.messageService.add(this.autoSaveService.AutoSavedDataRemovedMsg);
        });
    }

    getShipToAddress(): string {
        const shipTo = this.customerAssignments.value.customers
            .find(c => c.id === this.inboundLoadForm.controls.poolerId.value)
            ?.customerSites
            ?.find(cs => cs.id === this.inboundLoadForm.controls.shipFromLocation.value);

        if (!shipTo)
            return 'Select a Ship From';

        if (!shipTo.address1)
            return 'No Address';

        return `${shipTo.address1} ${shipTo.city}, ${shipTo.state} ${shipTo.postalCode}`;
    }

    get totalQuantity(): number {
        return this.inboundLoadLineItems?.length > 0 ? this.inboundLoadLineItems.map(inboundLoadLineItem => inboundLoadLineItem.quantity).reduce((a, b) => +a + +b) : 0;
   }
}
